import { pdf } from "@react-pdf/renderer";
import React from "react";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { triggerDownload } from "src/services/dataTransfer";
import * as DashboardChart from "./DashboardChart";
import { DATA_EXPORT_ID } from "./DashboardChart";
import { ChartImage, DashboardPdf, StatusCardData } from "./DashboardPdf";
import * as StatusCard from "./StatusCard";
import { STATUS_SUMMARY_EXPORT_ID } from "./StatusSummary";

type UseDashboardExportParams = {
  enrollmentPeriodName: string;
  formName: string;
  hasSchoolSelectionSection: boolean;
};

export function useDashboardExport({
  enrollmentPeriodName,
  formName,
  hasSchoolSelectionSection,
}: UseDashboardExportParams) {
  const [isExporting, setIsExporting] = React.useState(false);
  const toast = useAvelaToast();

  const exportToPdf = React.useCallback(async () => {
    setIsExporting(true);

    try {
      // Extract status summary data
      let statusCards: StatusCardData[] = [];
      const statusSummaryEl = document.querySelector<HTMLElement>(
        `[${DATA_EXPORT_ID}="${STATUS_SUMMARY_EXPORT_ID}"]`
      );
      if (statusSummaryEl) {
        statusCards = StatusCard.exportStatusCards(statusSummaryEl);
      }

      // Capture each chart
      const chartElements = document.querySelectorAll<HTMLElement>(
        `[${DATA_EXPORT_ID}]:not([${DATA_EXPORT_ID}='${STATUS_SUMMARY_EXPORT_ID}'])`
      );
      const chartImages: ChartImage[] = [];
      for (const el of chartElements) {
        const exportId = el.getAttribute(DATA_EXPORT_ID);
        if (!exportId) continue;
        try {
          const data = await DashboardChart.exportDashboardChart(el);
          chartImages.push({
            exportId,
            title: data.title,
            caption: data.caption,
            dataUrl: data.dataUrl,
          });
        } catch (err) {
          console.error(err);
          // Skip charts that fail to export (e.g. no SVG found)
        }
      }

      // Build PDF

      const preview = (
        <DashboardPdf
          enrollmentPeriodName={enrollmentPeriodName}
          formName={formName}
          statusCards={statusCards}
          chartImages={chartImages}
          hasSchoolSelectionSection={hasSchoolSelectionSection}
        />
      );
      const blob = await pdf(preview).toBlob();

      await triggerDownload(
        new Blob([blob], { type: "application/pdf" }),
        `${formName} - Dashboard.pdf`
      );
    } catch (error) {
      toast({
        title: "Export failed",
        description: "An error occurred while generating the PDF.",
        status: "error",
      });
    } finally {
      setIsExporting(false);
    }
  }, [enrollmentPeriodName, formName, hasSchoolSelectionSection, toast]);

  return { exportToPdf, isExporting };
}
